/**
 * PostHog session policy:
 * - Grabar: visitantes anónimos y usuarios Clerk con role `user` (sitio público).
 * - No grabar: rutas /admin (y /reels), ni cuentas Clerk admin/super_admin.
 * - cookie_consent=declined → opt-out total (sin capture ni replay).
 *
 * Session recording is re-asserted on every route/user change when allowed
 * (if the recorder stopped mid-session we restart it — do not rely only on a
 * one-shot lastAppliedPolicy flag).
 */

import type { UserResource } from "@clerk/types";
import { isClerkAdmin } from "@/lib/auth";
import { getStoredCookieConsent } from "@/lib/cookieConsentStorage";
import { isAdminShellPath } from "@/lib/geoConsentRoutes";

const EXTRA_DISABLED_EXACT = ["/reels"] as const;

/** Dual CTA admin tools still load PostHog (events); full admins never get replay. */
function isDualCtaAdminToolPath(pathname: string): boolean {
  return (
    pathname.startsWith("/admin/dual-cta-home") ||
    pathname.startsWith("/admin/dual-cta-templates") ||
    pathname.startsWith("/admin/video-ab-slots")
  );
}

/** True when pathname must not load or send PostHog (e.g. /admin/*, /dashboard/*). */
export function isPostHogDisabledPath(pathname: string): boolean {
  if (isDualCtaAdminToolPath(pathname)) return false;
  if (isAdminShellPath(pathname)) return true;
  return EXTRA_DISABLED_EXACT.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

/** Session replay bloqueado por ruta o por rol admin de Clerk. */
export function isPostHogRecordingBlocked(
  pathname: string,
  user?: UserResource | null,
): boolean {
  if (isDualCtaAdminToolPath(pathname)) {
    // Still block full Clerk admins on dual-cta tools.
    if (user && isClerkAdmin(user)) return true;
    return false;
  }
  if (isPostHogDisabledPath(pathname)) return true;
  if (user && isClerkAdmin(user)) return true;
  return false;
}

type PolicyState = "recording" | "blocked" | null;
let lastAppliedPolicy: PolicyState = null;

/** Reset sticky policy (e.g. after cookie accept/decline) so next apply re-runs. */
export function resetPostHogSessionPolicyCache(): void {
  lastAppliedPolicy = null;
}

function getPh():
  | {
      __loaded?: boolean;
      opt_out_capturing?: () => void;
      opt_in_capturing?: () => void;
      stopSessionRecording?: () => void;
      startSessionRecording?: (force?: boolean) => void;
      sessionRecordingStarted?: () => boolean;
    }
  | undefined {
  if (typeof window === "undefined") return undefined;
  return window.posthog as typeof window.posthog & {
    startSessionRecording?: (force?: boolean) => void;
  };
}

/**
 * Force opt-in + start session recording (public site).
 * Safe to call multiple times. Returns true if recorder reports started.
 */
export function ensurePostHogSessionRecording(): boolean {
  const ph = getPh();
  if (!ph?.__loaded) return false;
  try {
    ph.opt_in_capturing?.();
    // force=true overrides remote sample/linked-flag pause when SDK supports it
    ph.startSessionRecording?.(true);
    return Boolean(ph.sessionRecordingStarted?.());
  } catch {
    return false;
  }
}

/**
 * Aplica opt-in/out y start/stop replay según ruta + rol Clerk + cookie consent.
 * Llamar en cada cambio de ruta y cuando Clerk carga o cambia el usuario.
 */
export function applyPostHogSessionPolicy(
  pathname: string,
  user?: UserResource | null,
): void {
  if (typeof window === "undefined") return;
  const ph = getPh();
  if (!ph?.__loaded) return;

  const blocked = isPostHogRecordingBlocked(pathname, user);
  const cookieConsent = getStoredCookieConsent();
  const consentDeclined = cookieConsent === "declined";
  const shouldBlock = blocked || consentDeclined;

  if (shouldBlock) {
    if (lastAppliedPolicy !== "blocked") {
      try {
        ph.stopSessionRecording?.();
        ph.opt_out_capturing?.();
      } catch {
        /* ignore */
      }
      lastAppliedPolicy = "blocked";
    }
    return;
  }

  // Allowed: always re-assert recording (covers mid-session stop, late /decide, etc.)
  try {
    ph.opt_in_capturing?.();
    const already = Boolean(ph.sessionRecordingStarted?.());
    if (!already) {
      ph.startSessionRecording?.(true);
    }
  } catch {
    /* ignore */
  }
  lastAppliedPolicy = "recording";
}

/** @deprecated Usar applyPostHogSessionPolicy — mantiene compat sin rol Clerk. */
export function applyPostHogPathPolicy(pathname: string): void {
  applyPostHogSessionPolicy(pathname, null);
}
